import { useCallback, useState } from 'react'
import { useMutation } from 'react-query'
import { addToCart, checkout } from '../../requests'
import { Error } from '../../utils/apiErrorResponseAdapter'

interface CheckoutParams {
  // DEPRECATED: `sourceOrderId` is only used for replacement parts pilot.
  // Recurring replacement parts orders should use `subscriptionId` going forward.
  // If `subscriptionId` is provided, `sourceOrderId` will be ignored.
  sourceOrderId?: string
  subscriptionId?: string
  productId: number | undefined
  aobAcknowledged: boolean
}

const useCheckout = (
  { sourceOrderId, productId, aobAcknowledged, subscriptionId }: CheckoutParams,
  onError: (e: Error) => void
) => {
  const [isNavigating, setIsNavigating] = useState(false)

  const addToCartSucceeded = useCallback(
    (response: { cart: APIResponse.Cart }) => {
      const cartItems = response.cart.items.filter(
        (item) => item.productId === productId
      )

      checkoutMutation({
        cartItemIds: cartItems.length ? [cartItems[0].uuid] : [],
        sourceOrderId: subscriptionId ? undefined : sourceOrderId,
        subscriptionId,
        aobAcknowledged,
      })
    },
    [productId, sourceOrderId, aobAcknowledged]
  )

  const checkoutSucceeded = ({ checkoutPageUrl }: APIResponse.Checkout) => {
    setIsNavigating(true)
    window.location.href = checkoutPageUrl
  }

  const [addToCartMutation, { isLoading: addToCartLoading }] = useMutation(
    addToCart,
    {
      onSuccess: addToCartSucceeded,
      onError,
    }
  )

  const [checkoutMutation, { isLoading: checkoutLoading }] = useMutation(
    checkout,
    {
      onSuccess: checkoutSucceeded,
      onError,
    }
  )

  const performCheckout = useCallback(() => {
    if (!productId) return

    addToCartMutation({ cartItem: { productId, quantity: 1 } })
  }, [productId])

  return {
    checkout: performCheckout,
    isLoading: addToCartLoading || checkoutLoading,
    isNavigating,
  }
}

export default useCheckout
